import {
  Dimmer,
  Header,
  Icon,
  Loader,
  Sidebar,
  SidebarPushable,
  SidebarPusher,
} from '@mairistem/composants';
import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { RegisseurLogin } from '@/components/Regisseur';
import { getRegisseurs } from '@/routes';
import type { ApiResponseType, RegisseurType } from '@/types';
import { estEncaissementDirect } from '@/utils';
import { useRegieStore, useRegisseurStore, useUtilisateurStore } from '@/zustand';

const RegisseurContextProvider = ({ children }: { children: React.ReactNode }) => {
  const connectedRegisseur = useRegisseurStore((state) => state.regisseur);
  const setConnectedRegisseur = useRegisseurStore((state) => state.connect);
  const utilisateur = useUtilisateurStore((state) => state.utilisateur);
  const regie = useRegieStore((state) => state.regie);
  const hasRegies = useRegieStore((state) => state.hasRegies);

  const { regisseurConnexion } = regie || {};

  const isAutoConnectBranch = !regisseurConnexion || estEncaissementDirect();

  const [connecting, setConnecting] = React.useState<boolean>(_.isNil(connectedRegisseur));
  const [connectError, setConnectError] = React.useState<boolean>(false);

  const handleConnect = React.useCallback(
    (regisseurs?: RegisseurType[]) => {
      // Le login peut être partagé par plusieurs régisseurs (comptes homonymes) :
      // on prend celui qui a effectivement un mandat sur la régie sélectionnée,
      // pas forcément le premier de la liste.
      const regisseur = _.find(regisseurs, (r) =>
        _.some(r.regies, { identifiantRegie: regie?.identifiant }),
      );

      if (_.isNil(regisseur)) return false;

      // On récupère le mandat du régisseur sur la régie sélectionnée
      const regieRegisseur = _.find(
        regisseur.regies,
        (reg) => reg.identifiantRegie === regie?.identifiant,
      );

      if (_.isNil(regieRegisseur)) return false;

      setConnectedRegisseur({
        identifiant: utilisateur?.identifiant,
        identifiantRegisseur: regisseur.identifiant,
        administrateur: utilisateur?.profil.administrateur,
        mandat: regieRegisseur.mandat,
      });

      return true;
    },
    [regie, setConnectedRegisseur, utilisateur?.identifiant, utilisateur?.profil.administrateur],
  );

  React.useEffect(() => {
    const xRegisseur = connectedRegisseur?.identifiantRegisseur;

    const interceptor = request.interceptors.request.use((config) => {
      config.headers['X-Regisseur'] = xRegisseur;

      return config;
    });

    return () => {
      request.interceptors.request.eject(interceptor);
    };
  }, [connectedRegisseur]);

  React.useEffect(() => {
    if (!isAutoConnectBranch) {
      setConnecting(false);
      return;
    }

    // Si pas de double auth, on récupère le régisseur du login
    // et on le "connecte" automatiquement
    // Ou si en mode embedded, on bypass
    setConnecting(true);
    setConnectError(false);

    request
      .get<unknown, ApiResponseType<RegisseurType>>(
        getRegisseurs({ page: 1, identifiantLogin: utilisateur?.profil.identifiant }),
      )
      .then(({ data }) => {
        if (!handleConnect(data)) {
          setConnectError(true);
        }
      })
      .catch(() => {
        setConnectError(true);
      })
      .finally(() => {
        setConnecting(false);
      });
  }, [handleConnect, isAutoConnectBranch, utilisateur?.profil.identifiant]);

  const showChildren = !hasRegies || !_.isNil(connectedRegisseur);

  return (
    <SidebarPushable id="regie" className="regisseur-sidebar">
      <Sidebar
        data-testid="regisseur-sidebar"
        animation="overlay"
        icon="labeled"
        visible={hasRegies && regisseurConnexion && _.isNil(connectedRegisseur)}
        direction="top"
        width="very wide"
      >
        {regisseurConnexion && <RegisseurLogin onConnect={handleConnect} forcedLogin />}
      </Sidebar>
      <SidebarPusher>
        {showChildren
          ? children
          : isAutoConnectBranch &&
            (connectError ? (
              <Header
                icon
                style={{
                  display: 'flex',
                  height: '100%',
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
                size="huge"
                data-testid="error-message-regisseur-connect"
              >
                <Icon name="bug" />
                Un problème est survenu lors de la connexion du régisseur
                <br />
                <br />
                <Header.Subheader>
                  Veuillez réessayer ou contacter votre administrateur
                </Header.Subheader>
              </Header>
            ) : (
              connecting && (
                <Dimmer active inverted>
                  <Loader inverted>Connexion du régisseur</Loader>
                </Dimmer>
              )
            ))}
      </SidebarPusher>
    </SidebarPushable>
  );
};

export default RegisseurContextProvider;
